/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * FieldMappingSection — Accordion 4: semantic role → field dropdown mapping.
 * Roles: title_field, subtitle_field, image_field, description_field, badge_field, date_field.
 * When badge_field is set and the field has enum options, shows per-value color pickers.
 */

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CURRENCIES, getCurrencyDisplay, DEFAULT_CURRENCY, searchCurrencies } from '../../../utils/currencies';

const MAPPING_ROLES = [
  { key: 'title_field',       label: 'Title Field',       icon: 'bi-type-h1',        hint: 'Main heading / record name' },
  { key: 'subtitle_field',    label: 'Subtitle Field',    icon: 'bi-type-h2',        hint: 'Secondary heading below title' },
  { key: 'image_field',       label: 'Image Field',       icon: 'bi-image',          hint: 'Cover image (image data_type)' },
  { key: 'description_field', label: 'Description Field', icon: 'bi-text-paragraph', hint: 'Body text or summary' },
  { key: 'tags_field',        label: 'Tags',              icon: 'bi-tags',           hint: 'Comma or pipe-separated values shown as badges below description' },
  { key: 'badge_field',       label: 'Badge / Status',    icon: 'bi-tag',            hint: 'Status chip (enum recommended)' },
  { key: 'date_field',        label: 'Date Field',        icon: 'bi-calendar',       hint: 'Date for sorting / timeline' },
  { key: 'cost_field',        label: 'Cost / Price',      icon: 'bi-currency-dollar', hint: 'Price or cost amount' },
];

/**
 * CurrencyPicker — searchable dropdown for selecting a currency.
 *
 * The dropdown is rendered via a React portal into document.body so it escapes
 * any ancestor with overflow:hidden (e.g. the accordion's "overflow-hidden" class).
 * Its position is calculated from the trigger button's bounding rect.
 */
function CurrencyPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState({});
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  const selected = CURRENCIES.find(c => c.code === (value || DEFAULT_CURRENCY)) || CURRENCIES.find(c => c.code === DEFAULT_CURRENCY);
  const filtered = searchCurrencies(query);

  // Compute dropdown position from trigger rect each time it opens
  function computePosition() {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropH = 270; // approx max dropdown height (search bar + list)
    const openUpward = spaceBelow < dropH && rect.top > dropH;
    setDropdownStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      zIndex: 99999,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  }

  function openDropdown() {
    computePosition();
    setOpen(true);
  }

  // Close on outside click (checks both trigger and portal dropdown)
  useEffect(() => {
    function handler(e) {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
        setQuery('');
      }
    }
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Reposition on scroll/resize while open
  useEffect(() => {
    if (!open) return;
    const handler = () => computePosition();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus search when opened
  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  function handleSelect(code) {
    onChange(code);
    setOpen(false);
    setQuery('');
  }

  const dropdown = open && createPortal(
    <div
      ref={dropdownRef}
      role="listbox"
      style={{
        ...dropdownStyle,
        background: '#fff',
        border: '1.5px solid #e2e8f0',
        borderRadius: 10,
        boxShadow: '0 8px 30px rgba(0,0,0,0.16)',
        overflow: 'hidden',
      }}
    >
      {/* Search */}
      <div style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ position: 'relative' }}>
          <i
            className="bi bi-search"
            style={{
              position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)',
              color: '#94a3b8', fontSize: '0.72rem', pointerEvents: 'none',
            }}
          />
          <input
            ref={searchRef}
            type="text"
            className="form-control form-control-sm"
            placeholder="Search currency…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            style={{ paddingLeft: 28, fontSize: '0.8rem' }}
          />
        </div>
      </div>

      {/* List */}
      <div style={{ maxHeight: 220, overflowY: 'auto' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '10px 14px', color: '#94a3b8', fontSize: '0.8rem' }}>
            No currencies found
          </div>
        ) : (
          filtered.map(c => {
            const isActive = c.code === (value || DEFAULT_CURRENCY);
            return (
              <div
                key={c.code}
                role="option"
                aria-selected={isActive}
                onMouseDown={e => { e.preventDefault(); handleSelect(c.code); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 12px',
                  cursor: 'pointer',
                  background: isActive ? '#f0fdf4' : 'transparent',
                  borderLeft: isActive ? '3px solid #16a34a' : '3px solid transparent',
                  transition: 'background 100ms',
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = isActive ? '#f0fdf4' : 'transparent'; }}
              >
                <span style={{ fontWeight: 700, minWidth: 24, color: '#16a34a', fontSize: '0.85rem' }}>
                  {getCurrencyDisplay(c.code)}
                </span>
                <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', minWidth: 36 }}>
                  {c.code}
                </span>
                <span style={{ fontSize: '0.75rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.name}
                </span>
                {isActive && (
                  <i className="bi bi-check2" style={{ marginLeft: 'auto', color: '#16a34a', fontSize: '0.8rem' }} />
                )}
              </div>
            );
          })
        )}
      </div>
    </div>,
    document.body
  );

  return (
    <div style={{ flex: 1 }}>
      {/* Trigger button */}
      <button
        ref={triggerRef}
        type="button"
        className="form-select form-select-sm text-start"
        style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', width: '100%' }}
        onClick={() => (open ? (setOpen(false), setQuery('')) : openDropdown())}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span style={{ fontWeight: 700, minWidth: 22, color: '#16a34a' }}>
          {getCurrencyDisplay(selected?.code)}
        </span>
        <span style={{ color: '#374151' }}>{selected?.code}</span>
        <span style={{ color: '#94a3b8', fontSize: 11, marginLeft: 2 }}>– {selected?.name}</span>
      </button>

      {dropdown}
    </div>
  );
}

// Bootstrap badge colour palette for quick selection
const BADGE_PALETTE = [
  { label: 'Blue',   value: 'primary',   bg: '#2d6cf6' },
  { label: 'Green',  value: 'success',   bg: '#16a34a' },
  { label: 'Amber',  value: 'warning',   bg: '#d97706' },
  { label: 'Red',    value: 'danger',    bg: '#dc2626' },
  { label: 'Teal',   value: 'info',      bg: '#0891b2' },
  { label: 'Gray',   value: 'secondary', bg: '#64748b' },
];

function BadgeColorRow({ optionValue, current, onChange }) {
  return (
    <div className="d-flex align-items-center gap-2 py-1">
      <span className="small flex-shrink-0" style={{ minWidth: 100, color: '#374151' }}>
        {optionValue}
      </span>
      <div className="d-flex gap-1 flex-wrap">
        {BADGE_PALETTE.map(p => {
          const isActive = (current || 'primary') === p.value;
          return (
            <button
              key={p.value}
              type="button"
              title={p.label}
              onClick={() => onChange(p.value)}
              style={{
                width: 20, height: 20,
                borderRadius: '50%',
                background: p.bg,
                border: isActive ? `2px solid #1e293b` : '2px solid transparent',
                boxShadow: isActive ? `0 0 0 2px ${p.bg}66` : 'none',
                cursor: 'pointer',
                padding: 0,
                transition: 'box-shadow 120ms',
              }}
            />
          );
        })}
      </div>
      <span
        className="badge rounded-pill small flex-shrink-0"
        style={{
          background: BADGE_PALETTE.find(p => p.value === (current || 'primary'))?.bg || '#64748b',
          color: '#fff',
          fontSize: '0.68rem',
          padding: '2px 10px',
        }}
      >
        {optionValue}
      </span>
    </div>
  );
}

/**
 * TagColorPicker — preset swatches + native color input for choosing the
 * tag badges' background color. Mirrors the ColorPicker pattern used in
 * the page-builder editors (see DataCardsEditor.js).
 */
function TagColorPicker({ value, onChange, presets }) {
  return (
    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
      {presets.map(p => (
        <button
          key={p.color + p.label}
          type="button"
          title={p.label}
          onClick={() => onChange(p.color)}
          style={{
            width: 22, height: 22, borderRadius: 4, padding: 0, flexShrink: 0, cursor: 'pointer',
            border: value === p.color ? '2px solid #334155' : '1px solid #cbd5e1',
            background: p.color,
          }}
        />
      ))}
      <input
        type="color"
        value={value || '#6366f1'}
        onChange={e => onChange(e.target.value)}
        style={{ width: 28, height: 22, padding: 1, border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }}
      />
      {value && <span style={{ fontSize: 10, color: '#64748b', fontFamily: 'monospace' }}>{value}</span>}
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          style={{ fontSize: 10, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          Reset
        </button>
      )}
    </div>
  );
}

export default function FieldMappingSection({ config, updateConfig, fields = [], theme }) {
  const mapping = config.field_mapping || {};

  const tagBgPresets = [
    { label: 'Theme',      color: theme?.primary || '#6366f1' },
    { label: 'White',      color: '#ffffff' },
    { label: 'Light Gray', color: '#f1f5f9' },
    { label: 'Dark',       color: '#1e293b' },
  ];

  function handleRoleChange(role, fieldName) {
    updateConfig({ field_mapping: { ...mapping, [role]: fieldName || null } });
  }

  function handleCurrencyChange(currencyCode) {
    updateConfig({ field_mapping: { ...mapping, cost_currency: currencyCode } });
  }

  function handleTagColorChange(color) {
    updateConfig({ field_mapping: { ...mapping, tag_bg_color: color || null } });
  }

  function handleBadgeColorChange(optionValue, colorKey) {
    const badgeColors = { ...(mapping.badge_colors || {}), [optionValue]: colorKey };
    updateConfig({ field_mapping: { ...mapping, badge_colors: badgeColors } });
  }

  const allFields = fields;

  // Set of all field names currently assigned to any role
  const assignedNames = new Set(
    MAPPING_ROLES.map(r => mapping[r.key]).filter(Boolean)
  );

  // Options available for a given role:
  //  • exclude fields already mapped to OTHER roles
  //  • for date_field: additionally restrict to date/datetime data types
  function optionsFor(role) {
    const current = mapping[role.key];
    let candidates = allFields.filter(
      f => !assignedNames.has(f.name) || f.name === current
    );
    if (role.key === 'date_field') {
      candidates = candidates.filter(
        f => f.data_type === 'date' || f.data_type === 'datetime'
      );
    }
    if (role.key === 'cost_field') {
      candidates = candidates.filter(
        f => f.data_type === 'number' || f.data_type === 'decimal' || f.data_type === 'integer' || f.data_type === 'float'
      );
    }
    return candidates;
  }

  // Resolve enum options for the selected badge field
  const badgeFieldObj = mapping.badge_field
    ? fields.find(f => f.name === mapping.badge_field)
    : null;
  const badgeOptions = badgeFieldObj?.allowed_options
    ? badgeFieldObj.allowed_options.split(',').map(o => o.trim()).filter(Boolean)
    : [];

  // For Timeline layout, date_field is configured in the Layout section — hide it here.
  const isTimeline = config.view_type === 'timeline';
  const visibleRoles = isTimeline ? MAPPING_ROLES.filter(r => r.key !== 'date_field') : MAPPING_ROLES;

  return (
    <div>
      <div className="small text-muted mb-3">
        Map semantic roles to fields. These drive how each view type renders records.
      </div>
      <div className="d-flex flex-column gap-3">
        {visibleRoles.map(role => {
          const opts = optionsFor(role);
          const currentVal = mapping[role.key] || '';
          // If the current selection is no longer in opts (field deleted/filtered out), still show it
          const currentField = currentVal ? fields.find(f => f.name === currentVal) : null;
          return (
            <React.Fragment key={role.key}>
              <div className="d-flex align-items-center gap-3">
                <div style={{ width: 150, minWidth: 150 }}>
                  <div className="d-flex align-items-center gap-2 small fw-semibold">
                    <i className={`bi ${role.icon} text-primary`} />
                    {role.label}
                  </div>
                  <div className="text-muted" style={{ fontSize: 11 }}>{role.hint}</div>
                </div>
                <select
                  className="form-select form-select-sm"
                  value={currentVal}
                  onChange={e => handleRoleChange(role.key, e.target.value)}
                >
                  <option value="">— None —</option>
                  {currentField && !opts.find(f => f.name === currentField.name) && (
                    <option value={currentField.name}>
                      {currentField.display_name || currentField.name} ({currentField.data_type})
                    </option>
                  )}
                  {opts.map(f => (
                    <option key={f.name} value={f.name}>
                      {f.display_name || f.name} ({f.data_type})
                    </option>
                  ))}
                </select>
              </div>

              {/* Currency picker — shown directly below Cost/Price when a field is selected */}
              {role.key === 'cost_field' && currentVal && (
                <div className="d-flex align-items-center gap-3">
                  <div style={{ width: 150, minWidth: 150 }}>
                    <div className="d-flex align-items-center gap-2 small fw-semibold">
                      <i className="bi bi-cash-coin text-primary" />
                      Currency
                    </div>
                    <div className="text-muted" style={{ fontSize: 11 }}>Symbol shown with cost</div>
                  </div>
                  <CurrencyPicker
                    value={mapping.cost_currency || DEFAULT_CURRENCY}
                    onChange={handleCurrencyChange}
                  />
                </div>
              )}

              {/* Tag background colour — shown directly below Tags when a field is selected */}
              {role.key === 'tags_field' && currentVal && (
                <div className="d-flex align-items-center gap-3">
                  <div style={{ width: 150, minWidth: 150 }}>
                    <div className="d-flex align-items-center gap-2 small fw-semibold">
                      <i className="bi bi-palette text-primary" />
                      Tag Background
                    </div>
                    <div className="text-muted" style={{ fontSize: 11 }}>Background color for tag badges</div>
                  </div>
                  <TagColorPicker
                    value={mapping.tag_bg_color || ''}
                    onChange={handleTagColorChange}
                    presets={tagBgPresets}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Badge colour customisation — shown when badge_field is set and has enum options */}
      {mapping.badge_field && badgeOptions.length > 0 && (
        <div className="mt-4">
          <div className="small fw-semibold mb-1" style={{ color: theme?.primary }}>
            <i className="bi bi-palette me-1" />
            Badge Colours
          </div>
          <div className="small text-muted mb-2">
            Choose a colour for each status value.
          </div>
          <div
            className="border rounded p-3"
            style={{ background: '#f8fafc', borderColor: '#e2e8f0' }}
          >
            {badgeOptions.map(opt => (
              <BadgeColorRow
                key={opt}
                optionValue={opt}
                current={(mapping.badge_colors || {})[opt]}
                onChange={colorKey => handleBadgeColorChange(opt, colorKey)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
